// src/pages/InviteLinkPage.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import Spinner from "../components/Spinner";
import {
  TrashIcon,
  CheckIcon,
  PlusIcon,
  UserIcon,
  StarIcon,
  TagIcon,
  TicketIcon,
  MusicalNoteIcon,
  UserGroupIcon
} from "@heroicons/react/24/solid";
import { SERVER_BASE_URL } from "../config";

// ====================================
// Вспомогательные функции
// ====================================
const sumOfVariants = (vars) => {
  return vars.reduce((acc, v) => acc + v.limit, 0);
};

const getVariantDescription = (type) => {
  return type === "Free"
    ? "Guests that will have <strong>free</strong> entrance"
    : `Guests that will have a <strong>${type}</strong> discount`;
};

// Функция для отображения иконки категории
const renderCategoryIcon = (category) => {
  switch (category) {
    case "VIP":
      return <StarIcon className="h-4 w-4 text-yellow-500" title="VIP" />;
    case "BACKSTAGE":
      return <TagIcon className="h-4 w-4 text-gray-400" title="Backstage" />;
    case "ARTIST":
      return <MusicalNoteIcon className="h-4 w-4 text-purple-500" title="Artist" />;
    case "STAFF":
      return <UserGroupIcon className="h-4 w-4 text-blue-500" title="Staff" />;
    case "REGULAR":
    default:
      return <TicketIcon className="h-4 w-4 text-gray-400" title="Regular" />;
  }
};

// Function to parse guest input without truncating
const parseGuestInput = (input) => {
  // First extract any +N suffix
  const match = input.trim().match(/^(.+?)(?:\s*\+(\d+))?$/);
  if (match) {
    const fullName = match[1].trim();
    const plus = match[2] ? parseInt(match[2], 10) : 0;
    
    return {
      realName: fullName,
      plus: plus,
    };
  }
  return { realName: input, plus: 0 };
};

// ====================================
// Главный компонент InviteLinkPage
// ====================================
function InviteLinkPage() {
  const { token } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  // const [apiResponseDebug, setApiResponseDebug] = useState(null);

  // Функция для обновления списка гостей
  const refreshGuests = useCallback(async () => {
    if (!invitation) return;
    
    try {
      const guestsRes = await fetch(
        `${SERVER_BASE_URL}/api/guests/by_invitation/${invitation.id}`
      );
      
      if (guestsRes.ok) {
        const guestsData = await guestsRes.json();
        setGuests(guestsData);
      }
    } catch (error) {
      console.error("Error refreshing guests:", error);
    }
  }, [invitation]);

  // Загрузка приглашения и списка гостей при монтировании или изменении token
  useEffect(() => {
    async function loadData() {
      try {
        // Загрузка данных приглашения
        const res = await fetch(
          `${SERVER_BASE_URL}/api/invitations/by_token/${token}`
        );
        
        if (!res.ok) throw new Error("Invalid invitation or link");
        
        const data = await res.json();
        console.log("API Response (invitation):", data);
        // setApiResponseDebug(data);
        setInvitation(data);

        // Загрузка списка гостей
        if (data.id) {
          const guestsRes = await fetch(
            `${SERVER_BASE_URL}/api/guests/by_invitation/${data.id}`
          );
          
          if (guestsRes.ok) {
            const guestsData = await guestsRes.json();
            setGuests(guestsData);
          }
        }
      } catch (error) {
        console.error("Error loading invitation:", error);
      } finally {
        setLoading(false);
      }
    }
    
    loadData();
  }, [token]);

  // Состояние загрузки
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  // Приглашение не найдено
  if (!invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md w-full max-w-md">
          <p className="text-center text-red-500 font-medium">
            Invitation not found or link expired.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="container mx-auto max-w-3xl">
        <div className="bg-white rounded-lg shadow-md p-6 sm:p-8">
          {/* Header section */}
          <div className="mb-8 border-b border-gray-200 pb-6">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-3">
              Hello, <span className="text-indigo-600">{invitation.invitee_name}</span>!
            </h2>
            <p className="text-lg text-gray-700 mb-2">
              You are invited to the event:
            </p>
            <h3 className="text-2xl sm:text-4xl font-bold text-gray-900 mb-4">
              {invitation.event_name ? invitation.event_name : `${invitation.event_id}`}
            </h3>
            
            <div className="space-y-2">
              {/* Отображаем дату мероприятия */}
              {invitation.event_start_time && (
                <p className="text-gray-700">
                  on{" "}
                  <span className="font-medium text-indigo-700">
                    {new Date(invitation.event_start_time)
                      .toLocaleDateString("en-US", {
                        weekday: "short",
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                  </span>
                </p>
              )}
              
              {/* Отображаем место проведения */}
              {invitation.event_place && (
                <p className="text-gray-700">
                  at{" "}
                  <span className="font-medium text-indigo-700">
                    {invitation.event_place}
                  </span>
                </p>
              )}
            </div>
          </div>

          {/* Guest list info */}
          <div className="mb-6">
            <p className="text-lg font-medium text-gray-800 mb-2">
              You are invited to add{" "}
              <span className="font-bold text-indigo-600">{sumOfVariants(invitation.variants)}</span>{" "}
              guests in total.
            </p>
            <p className="text-sm text-gray-500 italic mb-1">
              Please enter the first and last name for each guest. Optionally,
              append a plus sign and a number (e.g., +1) if your guest will bring
              additional friends.
            </p>
            {/* <p className="text-sm text-red-500 font-medium">
              Important: You must enter exactly two words (first name and last name) for each guest.
            </p> */}
          </div>

          {/* Display invitee category if available */}
          {invitation.guest_category && (
            <div className="mb-6">
              <p className="text-sm text-gray-700 flex items-center">
                <span className="mr-2">Your category:</span>
                <span className="flex items-center bg-indigo-50 text-indigo-800 py-1 px-2 rounded-full">
                  {renderCategoryIcon(invitation.guest_category)}
                  <span className="ml-1 font-medium">{invitation.guest_category}</span>
                </span>
              </p>
            </div>
          )}

          {/* Variant sections */}
          <div className="space-y-8">
            {invitation.variants
              .filter((variant) => variant.limit > 0)
              .map((v) => (
                <VariantSection
                  key={v.id}
                  variant={v}
                  invitation={invitation}
                  guests={guests}
                  refreshGuests={refreshGuests}
                />
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ====================================
// Компонент VariantSection
// ====================================
function VariantSection({ variant, invitation, guests, refreshGuests }) {
  const admissionInDB = variant.type === "Free" ? "FREE" : variant.type;
  
  // Фильтруем гостей по invitation_id и типу
  // ВАЖНО: мы исключаем самого приглашаемого из подсчетов
  const variantGuests = guests.filter(
    (g) => g.invitation_id === invitation.id && 
           g.admission === admissionInDB && 
           g.name !== invitation.invitee_name &&
           g.category === variant.category // Добавить эту строку
  );
  
  // Подготовка рядов для отображения
  const prepareRows = () => {
    const rows = [];
    let usedSpots = 0;
    let rowIndex = 1;

    // Обрабатываем только реальных гостей (не приглашаемого)
    for (const guest of variantGuests) {
      const guestPlus = guest.plus || 0;
      const totalSpots = 1 + guestPlus;
      
      // Добавляем основной ряд с именем гостя
      rows.push({
        rowIndex: rowIndex++,
        existingGuest: guest,
        isPhantom: false,
        originalName: guest.name,
        plus: guestPlus
      });
      
      // Добавляем фантомные ряды для "+N" с правильной привязкой к основному гостю
      for (let i = 0; i < guestPlus; i++) {
        rows.push({
          rowIndex: rowIndex++,
          existingGuest: null,
          isPhantom: true,
          originalName: guest.name,
          phantomOwner: guest.id,
          phantomIndex: i + 1
        });
      }
      
      usedSpots += totalSpots;
    }
    
    // Добавляем оставшиеся пустые ряды до лимита
    const remainingRows = Math.max(0, variant.limit - usedSpots);
    for (let i = 0; i < remainingRows; i++) {
      rows.push({
        rowIndex: rowIndex++,
        existingGuest: null,
        isPhantom: false
      });
    }
    
    return rows;
  };
  
  const rows = prepareRows();
  
  // Правильный подсчет использованных билетов, исключая приглашаемого
  const usedTickets = variantGuests.reduce(
    (acc, guest) => acc + 1 + (guest.plus || 0),
    0
  );

  return (
    <div className="rounded-lg border border-gray-200 p-4 sm:p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          <span
            dangerouslySetInnerHTML={{
              __html: getVariantDescription(variant.type),
            }}
          />
        </h3>
        <div className="flex items-center space-x-2">
          {/* Отображаем категорию */}
          <span className="flex items-center bg-indigo-50 text-indigo-800 py-1 px-2 rounded-full">
            {renderCategoryIcon(variant.category)}
            <span className="ml-1 text-xs font-medium">{variant.category}</span>
          </span>
          <span className="text-sm bg-indigo-100 text-indigo-800 py-1 px-2 rounded-full">
            limit: {variant.limit}
          </span>
        </div>
      </div>
      
      <div className="space-y-3">
        {rows.map((row, idx) => {
          if (row.isPhantom) {
            // Render phantom row for "+N" guests with better identification
            return (
              <PhantomGuestRow 
                key={`phantom-${row.phantomOwner}-${row.phantomIndex}`}
                rowIndex={row.rowIndex}
                guestName={row.originalName}
                phantomIndex={row.phantomIndex}
              />
            );
          } else {
            // Render normal guest row
            const disableRow = !row.existingGuest && usedTickets >= variant.limit;
            
            return (
              <GuestRow
                key={row.existingGuest ? `guest-${row.existingGuest.id}` : `new-guest-${idx}`}
                rowIndex={row.rowIndex}
                type={variant.type}
                invitationId={invitation.id}
                eventId={invitation.event_id}
                invitationToken={invitation.token}
                via={invitation.invitee_name}
                existingGuest={row.existingGuest}
                guests={guests}
                variantGuests={variantGuests}
                variantLimit={variant.limit}
                disabled={disableRow}
                refreshGuests={refreshGuests}
                category={variant.category} // Передаем категорию варианта
                invitation={invitation} // Передаем все приглашение
              />
            );
          }
        })}
      </div>
    </div>
  );
}

// ====================================
// Компонент PhantomGuestRow для "+N"
// ====================================
function PhantomGuestRow({ rowIndex, guestName, phantomIndex }) {
  return (
    <div className="flex items-center w-full gap-2">
      <span className="text-gray-400 font-medium w-6">{rowIndex}.</span>
      <div className="relative flex-grow">
        <div className="w-full px-3 py-2 rounded-md bg-gray-100 border border-gray-200 flex items-center">
          <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
          <span className="text-gray-500">{guestName}</span>
          <span className="text-gray-400 ml-1">(+{phantomIndex})</span>
        </div>
      </div>
      <div className="min-w-[70px] h-10"></div>
    </div>
  );
}

// ====================================
// Компонент GuestRow с улучшенной обработкой ошибок и валидацией
// ====================================
function GuestRow({
  rowIndex,
  type,
  invitationId,
  eventId,
  invitationToken,
  via,
  existingGuest,
  guests,
  variantGuests,
  variantLimit,
  disabled,
  refreshGuests,
  category, // Категория варианта
  invitation // Все приглашение
}) {
  // Для существующего гостя база хранит чистое имя без "+N"
  const initialParsed = existingGuest
    ? { realName: existingGuest.name, plus: existingGuest.plus || 0 }
    : { realName: "", plus: 0 };
    
  const initialName =
    existingGuest && existingGuest.plus > 0
      ? `${existingGuest.name} +${existingGuest.plus}`
      : existingGuest
      ? existingGuest.name
      : "";
      
  const [name, setName] = useState(initialName);
  const [error, setError] = useState("");
  const [isSaved, setIsSaved] = useState(!!existingGuest);
  const [isChanged, setIsChanged] = useState(false);
  const [isEditing, setIsEditing] = useState(!existingGuest);

  const handleNameChange = (e) => {
    const value = e.target.value;
    setName(value);
    const parsed = parseGuestInput(value);
    if (
      existingGuest &&
      parsed.realName === initialParsed.realName &&
      parsed.plus === initialParsed.plus
    ) {
      setIsChanged(false);
    } else {
      setIsChanged(true);
    }
    setError("");
  };

  // Updated name validation function - accepts exactly 2 words
  const validateName = (val) => {
    // Extract just the name part (without the +N suffix)
    const nameOnly = val.trim().split(/\+\d+$/)[0].trim();
    
    // Split into words and check that there are exactly 2 words
    const words = nameOnly.split(/\s+/);
    
    // Check if there are exactly 2 words and both are not empty
    return words.length === 2 && words[0] && words[1];
  };

  // Проверка на дублирование имен
  const isDuplicateName = (inputName) => {
    const { realName } = parseGuestInput(inputName);
    if (!guests || !guests.length) return false;
    
    return guests.some((guest) => {
      const currentId = existingGuest ? String(existingGuest.id) : null;
      return (
        guest.name.trim().toLowerCase() === realName.trim().toLowerCase() &&
        String(guest.id) !== currentId
      );
    });
  };

  // Проверка, не превышает ли обновление квоту
  // Важно: исключаем самого приглашаемого из подсчетов
  const validatePlusQuota = (newPlus) => {
    const othersUsed = variantGuests
      .filter((guest) => 
        (!existingGuest || guest.id !== existingGuest.id) && 
        guest.name !== invitation.invitee_name
      )
      .reduce((acc, guest) => acc + 1 + (guest.plus || 0), 0);
    
    const newTotalUsed = othersUsed + 1 + newPlus;
    return newTotalUsed <= variantLimit;
  };

  // Improved save handler with better error messages
  const handleSave = async () => {
    const { realName, plus } = parseGuestInput(name);

    // If name is empty, handle as deletion
    if (realName.trim() === "") {
      if (existingGuest && existingGuest.id) {
        await deleteGuest();
      }
      return;
    }

    // Check name format: exactly 2 words
    if (!validateName(realName)) {
      setError("Please enter exactly as shown in the example: 'Firstname Lastname +N'. Only first and last name are allowed.");
      return;
    }
    
    // Check name length
    if (realName.length > 50) {
      setError("Name is too long. Please use a shorter name.");
      return;
    }
    
    // Check for duplicate name on client
    if (isDuplicateName(name)) {
      setError(`Guest "${realName}" is already in the list.`);
      return;
    }
    
    // Check quota
    if (!validatePlusQuota(plus)) {
      setError("Cannot update: Quota exceeded.");
      return;
    }

    const admissionType = type === "Free" ? "FREE" : type;
    const payload = {
      event_id: eventId,
      names: realName,
      plus: plus,
      admission: admissionType,
      invitation_id: invitationId,
      invitation_token: invitationToken,
      via: via,
      category: category, // ВАЖНО: используем категорию варианта
    };

    console.log("Saving guest with payload:", payload);

    try {
      let res;
      if (existingGuest && existingGuest.id) {
        res = await fetch(
          `${SERVER_BASE_URL}/api/guests/invite/update/${existingGuest.id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          }
        );
      } else {
        res = await fetch(`${SERVER_BASE_URL}/api/guests/invite`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
      }
      
      // Здесь мы обрабатываем ответ сервера
      let responseData;
      try {
        // Пытаемся разобрать ответ как JSON
        responseData = await res.json();
      } catch (e) {
        // Если ответ не JSON, продолжаем без него
        console.error("Response is not valid JSON:", e);
      }
      
      if (!res.ok) {
        console.error("Server error response:", responseData);
        
        // Обработка ошибок дублирования имени
        if (responseData && responseData.detail) {
          const errorDetail = responseData.detail.toLowerCase();
          
          // Проверяем различные признаки ошибки дублирования
          if (errorDetail.includes("duplicate") || 
              errorDetail.includes("already exists") ||
              errorDetail.includes("already in") ||
              errorDetail.includes("already on") ||
              errorDetail.includes("уже есть в списке") ||
              errorDetail.includes("ошибка сохранения")) {
            
            // Устанавливаем пользовательский текст ошибки для дублирования
            setError(`Guest "${realName}" is already in the list.`);
          } else {
            // Показываем оригинальную ошибку от сервера
            setError(responseData.detail);
          }
        } else {
          // Стандартная ошибка, если нет подробностей от сервера
          setError(`Guest "${realName}" is already in the list.`);
        }
        return;
      }
      
      // Успешное сохранение
      setIsSaved(true);
      setIsChanged(false);
      setIsEditing(false);
      window.parent.postMessage({ type: "guestListUpdated", eventId }, "*");
      await refreshGuests();
    } catch (err) {
      console.error("Error saving guest:", err);
      
      // По умолчанию для ошибок сохранения считаем, что это дублирование имени
      setError(`Guest "${realName}" is already in the list.`);
    }
  };

  const deleteGuest = async () => {
    if (existingGuest && existingGuest.id) {
      try {
        const res = await fetch(
          `${SERVER_BASE_URL}/api/guests/invite/delete/${existingGuest.id}`,
          {
            method: "DELETE",
          }
        );
        
        if (!res.ok) {
          throw new Error("Failed to delete guest");
        }
        
        setIsSaved(false);
        setIsChanged(false);
        setIsEditing(true);
        setName("");
        setError("");
        window.parent.postMessage({ type: "guestListUpdated", eventId }, "*");
        await refreshGuests();
      } catch (err) {
        console.error("Error deleting guest:", err);
        setError("Error deleting guest.");
      }
    } else {
      // Reset the empty row
      setName("");
      setError("");
      setIsChanged(false);
      setIsSaved(false);
      setIsEditing(true);
    }
  };

  const handleTrashClick = async () => {
    await deleteGuest();
  };

  const { plus } = parseGuestInput(name);
  
  // Определяем, находимся ли мы в сохраненном, неизмененном состоянии
  const savedAndUnchanged = isSaved && !isChanged && name.trim() !== "";

  return (
    <div className="flex flex-col">
      <div className="flex items-center w-full gap-2">
        <span className="text-gray-700 font-medium w-6">{rowIndex}.</span>
        <div className="relative flex-grow">
          {isEditing || !isSaved ? (
            <input
              type="text"
              placeholder="Firstname Lastname +N"
              value={name}
              onChange={handleNameChange}
              onBlur={() => {
                // Validate on blur
                if (name.trim() !== "") {
                  const { realName } = parseGuestInput(name);
                  if (!validateName(realName)) {
                    setError("Please enter exactly as shown in the example: 'Firstname Lastname +N'. Only first and last name are allowed.");
                  } else if (realName.length > 50) {
                    setError("Name is too long. Please use a shorter name.");
                  }
                }
              }}
              disabled={disabled}
              className={`w-full pr-10 px-3 py-2 rounded-md border focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
                error ? "border-red-500" : "border-gray-300"
              } ${disabled ? "bg-gray-100 cursor-not-allowed" : "bg-white"}`}
            />
          ) : (
            <div 
              className="w-full pr-10 px-3 py-2 rounded-md border border-gray-300 bg-gray-100 text-gray-700 flex items-center"
            >
              {/* Отображаем иконку категории */}
              {renderCategoryIcon(category)}
              <span className="ml-2">{name}</span>
            </div>
          )}
          
          {name.trim() !== "" && (
            <button
              onClick={handleTrashClick}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-red-500 focus:outline-none"
              aria-label="Delete guest"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>

        {!savedAndUnchanged ? (
          <button
            onClick={handleSave}
            disabled={disabled}
            className={`rounded-md font-medium text-white min-w-[70px] h-10 flex items-center justify-center ${
              disabled
                ? "bg-gray-300 cursor-not-allowed"
                : "bg-indigo-600 hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500"
            }`}
          >
            Save
          </button>
        ) : (
          <div
            className="rounded-md font-medium text-white min-w-[70px] h-10 flex items-center justify-center bg-indigo-500"
          >
            {plus > 0 ? (
              <div className="flex items-center justify-center text-white font-medium">
                <PlusIcon className="h-3 w-3 mr-0.5" />
                {plus}
              </div>
            ) : (
              <CheckIcon className="h-5 w-5" />
            )}
          </div>
        )}
      </div>

      {error && (
        <div className="ml-8 text-red-500 text-sm mt-1">{error}</div>
      )}
      
      {disabled && !existingGuest && (
        <div className="ml-8 text-xs text-gray-500 italic mt-1">
          Quota fully used
        </div>
      )}
    </div>
  );
}

// PropTypes для компонентов
VariantSection.propTypes = {
  variant: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    limit: PropTypes.number.isRequired,
    category: PropTypes.string // Добавлена категория
  }).isRequired,
  invitation: PropTypes.shape({
    id: PropTypes.number.isRequired,
    event_id: PropTypes.number.isRequired,
    invitee_name: PropTypes.string,
    token: PropTypes.string.isRequired,
    event_name: PropTypes.string,
    event_start_time: PropTypes.string,
    event_place: PropTypes.string,
    guest_category: PropTypes.string // Добавлена категория для приглашаемого
  }).isRequired,
  guests: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      admission: PropTypes.string,
      invitation_id: PropTypes.number,
      plus: PropTypes.number,
      category: PropTypes.string // Добавлена категория
    })
  ).isRequired,
  refreshGuests: PropTypes.func.isRequired,
};

GuestRow.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  type: PropTypes.string.isRequired,
  invitationId: PropTypes.number.isRequired,
  eventId: PropTypes.number.isRequired,
  invitationToken: PropTypes.string.isRequired,
  via: PropTypes.string.isRequired,
  existingGuest: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    plus: PropTypes.number,
    category: PropTypes.string // Добавлена категория
  }),
  guests: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      admission: PropTypes.string,
      invitation_id: PropTypes.number,
      plus: PropTypes.number,
      category: PropTypes.string // Добавлена категория
    })
  ).isRequired,
  variantGuests: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      name: PropTypes.string,
      admission: PropTypes.string,
      invitation_id: PropTypes.number,
      plus: PropTypes.number,
      category: PropTypes.string // Добавлена категория
    })
  ).isRequired,
  variantLimit: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  refreshGuests: PropTypes.func.isRequired,
  category: PropTypes.string.isRequired, // Добавлена категория варианта
  invitation: PropTypes.object.isRequired // Добавлено полное приглашение
};

PhantomGuestRow.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  guestName: PropTypes.string.isRequired,
  phantomIndex: PropTypes.number.isRequired
};

export default InviteLinkPage;